import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useMemo, useContext, useEffect, useState, } from "react";
import { persistedConsentSchema, } from "./contracts.js";
export function useConsentController({ adapter, policyVersion, storageKey, delayMs = 0, now = () => new Date(), categories = ["essential"], }) {
    const categoryKey = categories.join("|");
    const categoryList = useMemo(() => [...new Set(categoryKey.split("|"))], [categoryKey]);
    const defaultPreferences = useMemo(() => Object.fromEntries(categoryList.map((category) => [category, category === "essential"])), [categoryList]);
    const [decision, setDecision] = useState("undecided");
    const [preferences, setPreferences] = useState(defaultPreferences);
    const [isHydrated, setIsHydrated] = useState(false);
    const [isVisible, setIsVisible] = useState(false);
    const [error, setError] = useState(null);
    useEffect(() => {
        let active = true;
        let timer;
        const hydrate = async () => {
            try {
                const parsed = persistedConsentSchema.safeParse(adapter ? await adapter.read(storageKey) : undefined);
                const currentDecision = parsed.success && parsed.data.policyVersion === policyVersion
                    ? parsed.data.decision
                    : "undecided";
                if (!active)
                    return;
                setDecision(currentDecision);
                setPreferences({
                    ...defaultPreferences,
                    ...(parsed.success ? parsed.data.categories : undefined),
                    essential: true,
                });
                setIsHydrated(true);
                if (currentDecision === "undecided") {
                    timer = setTimeout(() => setIsVisible(true), delayMs);
                }
            }
            catch (cause) {
                if (!active)
                    return;
                setError(cause instanceof Error ? cause : new Error("Consent read failed"));
                setIsHydrated(true);
                timer = setTimeout(() => setIsVisible(true), delayMs);
            }
        };
        void hydrate();
        return () => {
            active = false;
            if (timer)
                clearTimeout(timer);
        };
    }, [adapter, defaultPreferences, delayMs, policyVersion, storageKey]);
    const decide = async (nextDecision, nextPreferences = preferences) => {
        const value = persistedConsentSchema.parse({
            decision: nextDecision,
            policyVersion,
            decidedAt: now().toISOString(),
            categories: nextPreferences,
        });
        try {
            if (adapter)
                await adapter.write(storageKey, value);
            setError(null);
            setDecision(nextDecision);
            setPreferences(nextPreferences);
            setIsVisible(false);
        }
        catch (cause) {
            setError(cause instanceof Error ? cause : new Error("Consent write failed"));
        }
    };
    return {
        decision,
        isHydrated,
        isVisible,
        error,
        show: () => {
            if (isHydrated)
                setIsVisible(true);
        },
        accept: () => decide("accepted", Object.fromEntries(categoryList.map((category) => [category, true]))),
        reject: () => decide("rejected", defaultPreferences),
        preferences,
        setCategory: (category, enabled) => {
            if (category === "essential" || !categoryList.includes(category))
                return;
            setPreferences((current) => ({ ...current, [category]: enabled }));
        },
        savePreferences: () => decide("customized"),
    };
}
const ConsentContext = createContext(undefined);
export function ConsentProvider({ children, ...options }) {
    const controller = useConsentController(options);
    return (_jsx(ConsentContext.Provider, { value: controller, children: children }));
}
export function useCookieConsent() {
    const controller = useContext(ConsentContext);
    if (!controller) {
        throw new Error("useCookieConsent must be used within ConsentProvider");
    }
    return controller;
}
//# sourceMappingURL=consent.js.map